// Shared building blocks for the auth screen and the /account page: field
// styling that matches the host modals, better-auth error extraction, and a
// paste-friendly 6-digit OTP field.

import { useEffect, useRef, useState } from 'react';

export const FIELD_INPUT =
    'w-full bg-bg-2 border border-border rounded-md px-3 py-2.5 text-[13px] text-text font-sans outline-none transition-[border-color,box-shadow] duration-100 focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)] disabled:opacity-60';

export const LABEL = 'block text-[11.5px] font-medium text-text-dim mb-1.5';

export const BTN_PRIMARY =
    'w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl text-[13.5px] font-medium bg-accent text-white transition-[background,transform] duration-100 hover:bg-accent-2 active:translate-y-px disabled:opacity-60 disabled:cursor-not-allowed';

export const BTN_LINK =
    'text-text-muted hover:text-text underline-offset-2 hover:underline transition-colors';

// Resend cooldown shared by the OTP and account-deletion flows.
export function useCooldown(seconds = 30) {
    const [remaining, setRemaining] = useState(0);
    useEffect(() => {
        if (remaining <= 0) return;
        const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
        return () => clearTimeout(id);
    }, [remaining]);
    return { remaining, start: () => setRemaining(seconds) };
}

// Persist the auth-screen step + email for the lifetime of the tab so a
// remount or hard reload (e.g. alt-tab session refetch) keeps the user on the
// OTP step instead of dropping them back to the sign-in form — which would
// trigger a fresh OTP and invalidate the code they're holding (rotate strategy).
const VIEW_KEY = 'cotty:auth:view';
const EMAIL_KEY = 'cotty:auth:email';

export const authPersist = {
    view: (): string | null => {
        try {
            return sessionStorage.getItem(VIEW_KEY);
        } catch {
            return null;
        }
    },
    email: (): string => {
        try {
            return sessionStorage.getItem(EMAIL_KEY) ?? '';
        } catch {
            return '';
        }
    },
    save: (view: string, email: string) => {
        try {
            sessionStorage.setItem(VIEW_KEY, view);
            sessionStorage.setItem(EMAIL_KEY, email);
        } catch {
            /* storage unavailable — non-fatal */
        }
    },
    clear: () => {
        try {
            sessionStorage.removeItem(VIEW_KEY);
            sessionStorage.removeItem(EMAIL_KEY);
        } catch {
            /* noop */
        }
    },
};

// better-auth client calls resolve to { data, error }; error is
// { message?, code?, status? }. Pull a human message with a sane fallback.
export function authErrorMessage(
    error: { message?: string; code?: string } | null | undefined,
    fallback = 'Something went wrong. Please try again.',
): string {
    return error?.message?.trim() || fallback;
}

/**
 * Message for a REJECTED auth call. better-auth returns `{ error }` for
 * api-level failures but throws on transport failures (unreachable api, DNS,
 * TLS) — it never sets catchAllError. Every auth handler must funnel its
 * awaits through a try/catch using this, or a dead network leaves the
 * submit button spinning forever with nothing shown.
 */
export function transportErrorMessage(err: unknown): string {
    return err instanceof Error && err.message
        ? `Can't reach the server — ${err.message}`
        : "Can't reach the server.";
}

/** A single numeric field for a 6-digit code: native paste works, non-digits
 *  are stripped, and it auto-fires `onComplete` when full. */
export function OtpField({
    value,
    onChange,
    onComplete,
    disabled,
    autoFocus = true,
    length = 6,
}: {
    value: string;
    onChange: (v: string) => void;
    onComplete?: (v: string) => void;
    disabled?: boolean;
    autoFocus?: boolean;
    length?: number;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (autoFocus) ref.current?.focus();
    }, [autoFocus]);

    return (
        <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={length}
            disabled={disabled}
            value={value}
            onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, length);
                onChange(next);
                if (next.length === length) onComplete?.(next);
            }}
            className="w-full bg-bg-2 border border-border rounded-xl px-4 py-3 text-center font-mono text-[26px] tracking-[0.42em] text-text outline-none transition-[border-color,box-shadow] duration-100 focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)] disabled:opacity-60"
            placeholder="000000"
            aria-label="Verification code"
        />
    );
}

/** Inline error alert used across auth forms. */
export function AuthError({ message }: { message: string | null }) {
    if (!message) return null;
    return (
        <div
            role="alert"
            className="mt-3 px-3 py-2 rounded-md border border-danger/30 bg-danger-soft text-danger text-[12.5px]"
        >
            {message}
        </div>
    );
}
